<template>
  <div class="touch-drag-navigation" :class="{ 'is-dragging': isDragging }">
    <svg
      class="touch-drag-navigation-svg"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      :style="{
        '--drag-left': `${dragLeft}px`,
        '--drag-right': `${dragRight}px`,
      }"
    >
      <path :d="leftPath" class="left" />
      <path :d="rightPath" class="right" />
    </svg>

    <div
      class="arrow-icons"
      :style="{
        '--drag-left': `${dragLeft * 0.55}vw`,
        '--drag-right': `${dragRight * 0.55}vw`,
      }"
    >
      <FaIcon icon="chevron-left" class="arrow-icon arrow-icon-left" />
      <FaIcon icon="chevron-right" class="arrow-icon  arrow-icon-right" />
    </div>
  </div>
</template>

<script>
import FaIcon from './FaIcon'

/**
 * The possible drag directions
 * @enum {string}
 */
const DragDirection = {
  LEFT: 'left',
  RIGHT: 'right',
}

/**
 * Provides stepped horizontal touch navigation.
 * - Listens for touch events on the window
 * - Displays a visual feedback overlay
 * - Emits  custom events when the internal drag distance threshold is surpassed
 *
 * Please note: To prevent horizontal scroll of the parent container, you must add
 * `touch-action: pan-y pinch-zoom;` or similar to the parent container.
 *
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */
export default {
  name: 'TouchDragNavigation',
  components: { FaIcon },
  props: {
    lockNavigation: { type: Boolean, default: false },
  },
  data() {
    return {
      /**
       * A flag to see if we're currently dragging
       * @type {boolean}
       */
      isDragging: false,

      /**
       * The current X position of the current drag
       * @type {number}
       */
      currentPointX: 0,

      /**
       * The first X position of the current drag
       * @type {number}
       */
      firstPointX: 0,

      /**
       * The current viewport width
       * @type {number}
       */
      currentViewportWidth: 0,

      /**
       * The viewport width when the current drag was started
       * @type {number}
       */
      firstViewportWidth: 0,
    }
  },
  computed: {
    /**
     * Determines the direction in which the user is dragging
     * @returns {DragDirection}
     */
    dragDirection() {
      return this.firstPointX - this.currentPointX > 0
        ? DragDirection.LEFT
        : DragDirection.RIGHT
    },

    /**
     * Returns the absolute distance currently dragged in pixels
     * @returns {number}
     */
    dragDistance() {
      return Math.abs(this.firstPointX - this.currentPointX)
    },

    /**
     * Returns the absolute distance currently dragged in percentage of the viewport
     * @returns {number}
     */
    dragDistanceInPercent() {
      return (this.dragDistance / this.currentViewportWidth) * 100 || 0
    },

    /**
     * Returns the absolute distance currently dragged to the left in percentage
     * @returns {number}
     */
    dragLeft() {
      return this.dragDirection === DragDirection.LEFT
        ? this.dragDistanceInPercent * -1
        : 0
    },

    /**
     * Returns the absolute distance currently dragged to the right in percentage
     * @returns {number}
     */
    dragRight() {
      return this.dragDirection === DragDirection.RIGHT
        ? this.dragDistanceInPercent
        : 0
    },

    /**
     * Returns the SVG path for the left piece of graphic
     * @returns {string}
     */
    leftPath() {
      if (this.dragDirection === DragDirection.RIGHT) {
        return this.generatePath(DragDirection.LEFT)
      }

      return ''
    },

    /**
     * Returns the SVG path for the right piece of graphic
     * @returns {string}
     */
    rightPath() {
      if (this.dragDirection === DragDirection.LEFT) {
        return this.generatePath(DragDirection.RIGHT)
      }

      return ''
    },

    /**
     * Returns the amount of pixels by which the viewport width has changed since the drag started
     * @returns {number}
     */
    absoluteViewportChange() {
      return Math.abs(this.firstViewportWidth - this.currentViewportWidth)
    },
  },
  watch: {
    lockNavigation(lock) {
      if (lock) {
        this.onTouchEnd()
        this.tearDownEventHandling()
      } else {
        this.setUpEventHandling()
      }
    },

    /**
     * Keeps an eye on the drag distance and triggers a custom drag event when the drag distance exceeds 160 px
     * @param {number} newDragDistance
     * @emits `drag-left` or `drag-right`
     */
    dragDistance(newDragDistance) {
      // Cancel the touch event if the viewport width changes by more than 15 px,
      // since the user is most likely pinch zooming and not dragging to navigate.
      if (this.absoluteViewportChange > 15) {
        this.stopDragging()
        return
      }

      if (newDragDistance > 160) {
        this.$emit(`drag-${this.dragDirection}`)
        this.stopDragging()
      }
    },
  },

  /**
   * Installs the required event listeners when mounted
   */
  mounted() {
    this.setUpEventHandling()
  },

  /**
   * Removes the required event listeners before unmounted
   */
  beforeDestroy() {
    this.tearDownEventHandling()
  },
  methods: {
    /**
     * Generates a path for SVG graphic in the given position
     * @param {DragDirection} position
     * @returns {string}
     */
    generatePath(position) {
      const isLeft = position === DragDirection.LEFT
      const zeroX = isLeft ? 0 : 100
      const radius = this.dragDistanceInPercent * (isLeft ? 1 : -1)

      return `M${zeroX} 0 Q ${zeroX + radius} 50 ${zeroX} 100`
    },

    /**
     * Returns the X value for the given event.
     * The X value is the average X value across all fingers present in the given event,
     * since focusing on a single finger can trigger navigation on multi finger gestures such as pinch zoom.
     *
     * @param {Event} event
     * @returns {number}
     */
    findX(event) {
      // Extract the touching fingers from the event
      const fingers = Array.from(event.touches)

      return (
        fingers
          // Extract the X values from fingers
          .map(touch => touch.clientX)
          // Sum all of the values
          .reduce((a, b) => a + b, 0) /
        // Divide by the length of the array to get the average value
        fingers.length
      )
    },

    /**
     * Stops the current drag tracking and resets all related variables.
     */
    stopDragging() {
      this.isDragging = false
      this.firstPointX = 0
      this.currentPointX = 0
    },

    /**
     * Callback function for the touch start event.
     * @param {Event} event
     */
    onTouchStart(event) {
      const x = this.findX(event)

      this.isDragging = true
      this.firstPointX = x
      this.currentPointX = x
      this.firstViewportWidth = window.innerWidth
      this.currentViewportWidth = window.innerWidth
    },

    /**
     * Callback function for the touch end event.
     */
    onTouchEnd() {
      this.stopDragging()
    },

    /**
     * Callback function for the touch move event.
     * @param {Event} event
     */
    onTouchMove(event) {
      if (this.isDragging) {
        this.currentPointX = this.findX(event)
        this.currentViewportWidth = window.innerWidth
      }
    },

    /**
     * Sets up all required event handling
     */
    setUpEventHandling() {
      window.addEventListener('touchstart', this.onTouchStart)
      window.addEventListener('touchend', this.onTouchEnd)
      window.addEventListener('touchcancel', this.onTouchEnd)
      window.addEventListener('touchmove', this.onTouchMove)
    },

    /**
     * Tears down all event handling
     */
    tearDownEventHandling() {
      window.removeEventListener('touchstart', this.onTouchStart)
      window.removeEventListener('touchend', this.onTouchEnd)
      window.removeEventListener('touchcancel', this.onTouchEnd)
      window.removeEventListener('touchmove', this.onTouchMove)
    },
  },
}
</script>

<style lang="scss" scoped>
.touch-drag-navigation {
  --drag-left: 0;
  --drag-right: 0;
}

.touch-drag-navigation-svg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  path {
    fill: rgba(0, 0, 0, 0.2);
  }
}

.arrow-icon {
  position: fixed;
  top: 50%;
  font-size: 2rem;
  pointer-events: none;
}

.arrow-icon-left {
  left: -2em;
  transform: translate(var(--drag-right), -50%);
}

.arrow-icon-right {
  right: -2em;
  transform: translate(var(--drag-left), -50%);
}

.touch-drag-navigation:not(.is-dragging) {
  .arrow-icon {
    transition: transform 0.5s ease-out;
  }
}
</style>
